import { Component, type ErrorInfo, type ReactNode } from "react";
import { useI18n } from "../../i18n";

/**
 * Canvas editor error boundary.
 *
 * The Canvas editor is a large interactive surface; a single render-time throw
 * (e.g. during a block edit or a pointer gesture) used to unmount the WHOLE app
 * tree, leaving a black page that forced a reload and lost all unsaved work
 * (Claudio: "la pagina diventa tutta nera e devo refreshare e ricominciare da
 * capo"). This boundary contains the failure to the editor, keeps the app
 * alive, and SHOWS the actual error message so the exact cause is visible for
 * diagnosis instead of a silent blackout.
 */
interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class CanvasErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[CanvasErrorBoundary]", error, info.componentStack);
  }

  private dismiss = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return <CanvasErrorFallback error={error} onDismiss={this.dismiss} />;
  }
}

function CanvasErrorFallback({ error, onDismiss }: { error: Error; onDismiss: () => void }) {
  const { t } = useI18n();
  return (
    <div
      data-testid="canvas-error-boundary"
      className="flex h-full w-full items-center justify-center bg-[#06080d] p-8"
    >
      <div className="max-w-lg rounded-lg border border-[color:var(--neon-coral-ring)] bg-panel p-6 text-center">
        <h2 className="m-0 font-display text-[16px] font-semibold text-text-primary">
          {t("editor.error.title")}
        </h2>
        <p className="mt-2 text-[12px] text-text-muted">
          {t("editor.error.body")}
        </p>
        <pre
          data-testid="canvas-error-message"
          className="mt-3 max-h-40 overflow-auto rounded bg-obsidian p-3 text-left font-mono text-[10.5px] leading-relaxed text-neon-coral whitespace-pre-wrap"
        >
          {String(error.message || error)}
        </pre>
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            type="button"
            data-testid="canvas-error-dismiss"
            onClick={onDismiss}
            className="chip h-8"
          >
            {t("editor.error.dismiss")}
          </button>
          <button
            type="button"
            data-testid="canvas-error-reload"
            onClick={() => window.location.reload()}
            className="chip h-8"
          >
            {t("editor.error.reload")}
          </button>
        </div>
      </div>
    </div>
  );
}
